/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { theme } from '@marriott/mi-ui-library-shop';
import { jsonContent as mockLabels } from './mock/jsonContent';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { ErrorHandlerAlert } from '../../molecules';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { useHotelInformationServerDataStore } from '../../modules/store/hotelInformationServerDataStore';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';

const HotelInformation = React.lazy(() => import('./HotelInformation'));
interface HotelInformationProps {
  model: Record<string, any>;
  isAuthorMode?: boolean;
}

const HotelInformationComponent: React.FC<HotelInformationProps> & { renderCallServerSide: () => void } = ({
  model,
  isAuthorMode,
}) => {
  const { envVarsObject } = useClientEnvVarsStore();

  // Subscribe to searchResultsStore for actual hotel data
  const propertyInfoData = useSearchResultsStore((state: any) => state.propertyInfoData);

  const jsonContent: any = { ...mockLabels, ...model };
  const updatedTheme = {
    ...theme,
    // TODO Check how this data will come up in next architecture.
    // dir: initialState?.pageProps?.dirRTL === 'true' ? 'rtl' : '',
    dir: '',
  };

  // Initialize Zustand store with data from searchResultsStore + AEM labels
  useEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand) {
      // Extract actual hotel data from searchResultsStore
      const actualPropertyData = propertyInfoData?.propertyInfoData || {};
      const property = actualPropertyData?.property || {};

      // Get the store methods using getState() for stable reference
      const { hotelInformationInitialization } = useHotelInformationServerDataStore.getState();

      // Initialize store with REAL data from searchResultsStore + labels from AEM
      hotelInformationInitialization({
        hotelInformationData: actualPropertyData,
        propertyId: property?.id || '',
        jsonContent: jsonContent, // Labels from AEM model
        amenitiesData: {}, // Loaded separately via GraphQL in Amenities component
        accessibilityData: {},
        generalInformationData: {},
        brandCode: property?.basicInformation?.brand?.id || '',
        keyAmenities: model?.['keyAmenities'] || [],
        pageProps: model?.['pageProps'] || {},
        isAuthorMode: isAuthorMode,
      });
    }
  }, [propertyInfoData, model, envVarsObject, jsonContent, isAuthorMode]);

  // Extract all required props from model for HotelInformation component
  // This ensures Context flow works when flag is OFF
  const hotelInformationProps = {
    hotelInformationData: model?.['hotelInformationData'] || {},
    propertyId: model?.['propertyId'] || '',
    jsonContent: jsonContent,
    amenitiesData: model?.['amenitiesData'] || {},
    accessibilityData: model?.['accessibilityData'] || {},
    generalInformationData: model?.['generalInformationData'] || {},
    brandCode: model?.['brandCode'] || '',
    keyAmenities: model?.['keyAmenities'] || [],
    pageProps: model?.['pageProps'] || {},
    isAuthorMode: isAuthorMode,
  };

  const renderComponent = () => {
    return (
      <ErrorHandlerAlert alertMessage={jsonContent?.['componentErrorMessage']}>
        <Suspense fallback={''}>
          <ThemeProvider theme={updatedTheme}>
            <HotelInformation {...hotelInformationProps} />
          </ThemeProvider>
        </Suspense>
      </ErrorHandlerAlert>
    );
  };
  return renderComponent();
};

export const HotelInformationComponentConfig = {
  emptyLabel: 'HotelInformationComponent',
  isEmpty: false,
  resourceType: `mi-aem-shop-spa/components/content/hotelinformation`,
};

export const HotelInformationComponentEditable = (props: any) => {
  return props.cqPath.includes('experiencefragment') ? (
    <HotelInformationComponent {...props} />
  ) : (
    <EditableComponent config={{}} {...props}>
      <HotelInformationComponent {...props} />
    </EditableComponent>
  );
};

export default HotelInformationComponent;

HotelInformationComponent.renderCallServerSide = () => {
  return {
    customQueryData: 'this is test string',
  };
};
